import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, take, timeout } from 'rxjs/operators';

import {
  HOST_FAILOVER_REQUEST_TIMEOUT_MS,
  HOST_FAILOVER_RETRY_DONE,
  HOST_FAILOVER_RETRY_REQUEST
} from '@core/services/host-failover/host-failover.context';
import { HostFailoverService } from '@core/services/host-failover/host-failover.service';

@Injectable({ providedIn: 'root' })
export class HostFailoverInterceptor implements HttpInterceptor {
  constructor(private readonly hostFailoverService: HostFailoverService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.handleRequest(req, next).pipe(
      catchError((error) => {
        console.log(error);
        if (this.hostFailoverService.isHostUnavailableError(error)) {
          const canRetry = req.context.get(HOST_FAILOVER_RETRY_REQUEST) && !req.context.get(HOST_FAILOVER_RETRY_DONE);
          const switchHost$ = this.hostFailoverService
            .ensureAvailableHost({
              excludeHosts: [this.getRequestHost(req.url)]
            })
            .pipe(take(1));

          if (canRetry) {
            return switchHost$.pipe(
              switchMap((switched) => {
                if (!switched) {
                  return throwError(() => error);
                }

                return this.handleRequest(
                  req.clone({
                    url: this.hostFailoverService.buildUrlWithCurrentHost(req.url),
                    context: req.context.set(HOST_FAILOVER_RETRY_DONE, true)
                  }),
                  next
                );
              })
            );
          }

          switchHost$.subscribe();
        }

        return throwError(() => error);
      })
    );
  }

  private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const timeoutMs = req.context.get(HOST_FAILOVER_REQUEST_TIMEOUT_MS);
    const request$ = next.handle(req);

    return timeoutMs ? request$.pipe(timeout(timeoutMs)) : request$;
  }

  private getRequestHost(url: string): string {
    try {
      return new URL(url).origin;
    } catch (e) {
      return '';
    }
  }
}
